/**
 * Piezas compartidas de las pantallas contables: período en la URL, barra de
 * período, aviso de cifras provisorias y botones de exportación.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, FileSpreadsheet, FileText, Loader2 } from "lucide-react";

import { Button, Select } from "../../components/ui";
import { useToast } from "../../components/Toast";
import { fechaLarga, mesAnio, nombreMes } from "../../lib/format";
import { descargar, type FiltroPeriodo, type Periodo } from "./api";

function ultimoDia(anio: number, mes: number): string {
  const d = new Date(Date.UTC(anio, mes, 0)).getUTCDate();
  return `${anio}-${String(mes).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export interface PeriodoUrl {
  filtro: FiltroPeriodo;
  anio: number | undefined;
  mesDesde: number | undefined;
  mesHasta: number | undefined;
  cambiar: (c: {
    anio?: number | undefined;
    mesDesde?: number | undefined;
    mesHasta?: number | undefined;
    /** Año que se está mostrando: el rango de meses sólo filtra con año en la URL. */
    anioActual?: number;
  }) => void;
}

/** Período elegido, guardado en la URL (`?anio=&md=&mh=`) para compartir el link. */
export function usePeriodoUrl(): PeriodoUrl {
  const [params, setParams] = useSearchParams();
  const num = (k: string): number | undefined => {
    const v = Number(params.get(k));
    return Number.isFinite(v) && v > 0 ? v : undefined;
  };
  const anio = num("anio");
  const mesDesde = num("md");
  const mesHasta = num("mh");
  const filtro: FiltroPeriodo = {
    anio,
    desde: anio && mesDesde ? `${anio}-${String(mesDesde).padStart(2, "0")}-01` : undefined,
    hasta: anio && mesHasta ? ultimoDia(anio, mesHasta) : undefined,
  };
  const cambiar: PeriodoUrl["cambiar"] = (c) => {
    const next = new URLSearchParams(params);
    const set = (k: string, v: number | undefined): void => {
      if (v === undefined) next.delete(k);
      else next.set(k, String(v));
    };
    if ("anio" in c) {
      set("anio", c.anio);
      next.delete("md");
      next.delete("mh");
    }
    if (("mesDesde" in c || "mesHasta" in c) && !next.has("anio") && c.anioActual) {
      next.set("anio", String(c.anioActual));
    }
    if ("mesDesde" in c) set("md", c.mesDesde);
    if ("mesHasta" in c) set("mh", c.mesHasta);
    setParams(next, { replace: true });
  };
  return { filtro, anio, mesDesde, mesHasta, cambiar };
}

/** Año y rango de meses, con la fecha de actualización y lo publicado. */
export function BarraPeriodo({
  periodo,
  url,
  conDesde = true,
}: {
  periodo: Periodo | undefined;
  url: PeriodoUrl;
  conDesde?: boolean;
}): JSX.Element | null {
  if (!periodo || periodo.anio === null) return null;
  const anio = periodo.anio;
  const mesDesde = Number(periodo.desde.slice(5, 7));
  const mesHasta = Number(periodo.hasta.slice(5, 7));
  const meses = Array.from({ length: 12 }, (_, i) => i + 1);
  // Meses con movimientos: hasta el último dato cargado del año que se muestra.
  const ultimo = periodo.ultimo_dato;
  const mesUltimo = !ultimo
    ? 0
    : Number(ultimo.slice(0, 4)) > anio
      ? 12
      : Number(ultimo.slice(0, 4)) === anio
        ? Number(ultimo.slice(5, 7))
        : 0;
  const opcionMes = (m: number): JSX.Element => (
    <option
      key={m}
      value={m}
      className={m <= mesUltimo ? "font-medium text-neutral-900" : "text-neutral-500"}
    >
      {nombreMes(m)}
      {m <= mesUltimo ? "" : " · sin datos"}
    </option>
  );
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="w-28">
        <Select
          label="Año"
          value={String(anio)}
          onChange={(e) => url.cambiar({ anio: Number(e.target.value) })}
        >
          {periodo.anios_disponibles.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </Select>
      </div>
      {conDesde && (
        <div className="w-36">
          <Select
            label="Desde"
            value={String(mesDesde)}
            onChange={(e) => url.cambiar({ mesDesde: Number(e.target.value), anioActual: anio })}
          >
            {meses.map(opcionMes)}
          </Select>
        </div>
      )}
      <div className="w-36">
        <Select
          label="Hasta"
          value={String(mesHasta)}
          onChange={(e) => url.cambiar({ mesHasta: Number(e.target.value), anioActual: anio })}
        >
          {meses.map(opcionMes)}
        </Select>
      </div>
      <p className="pb-2 text-sm text-neutral-600">
        Datos al {fechaLarga(periodo.ultimo_dato)}
        {" · "}
        {periodo.publicado_hasta
          ? `publicado hasta ${mesAnio(periodo.publicado_hasta)}`
          : "sin meses publicados"}
      </p>
    </div>
  );
}

/** Aviso ámbar cuando el rango incluye meses que todavía pueden cambiar. */
export function AvisoProvisorio({ periodo }: { periodo: Periodo | undefined }): JSX.Element | null {
  if (!periodo || periodo.vacio || !periodo.provisorio_desde) return null;
  if (periodo.hasta < periodo.provisorio_desde) return null;
  return (
    <div
      role="note"
      className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 ring-1 ring-amber-200"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        <strong>Cifras provisorias desde {mesAnio(periodo.provisorio_desde)}.</strong> La oficina
        contable todavía no cierra esos meses: pueden cambiar.
      </span>
    </div>
  );
}

/** Botones Excel / PDF para un informe. */
export function BotonesExportar({
  recurso,
  params,
  soloExcel = false,
}: {
  recurso: "balance-8-columnas" | "mayor" | "comprobantes";
  params: FiltroPeriodo & { nivel?: number | undefined; cuenta_id?: number | undefined };
  soloExcel?: boolean;
}): JSX.Element {
  const toast = useToast();
  const [bajando, setBajando] = useState<"xlsx" | "pdf" | null>(null);
  const bajar = async (formato: "xlsx" | "pdf"): Promise<void> => {
    setBajando(formato);
    try {
      await descargar(recurso, formato, params);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo descargar el archivo");
    } finally {
      setBajando(null);
    }
  };
  const icono = (f: "xlsx" | "pdf", Icon: typeof FileText): JSX.Element =>
    bajando === f ? (
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
    ) : (
      <Icon className="h-4 w-4" aria-hidden="true" />
    );
  return (
    <div className="flex gap-2">
      <Button variant="secondary" onClick={() => void bajar("xlsx")} disabled={bajando !== null}>
        {icono("xlsx", FileSpreadsheet)} Excel
      </Button>
      {!soloExcel && (
        <Button variant="secondary" onClick={() => void bajar("pdf")} disabled={bajando !== null}>
          {icono("pdf", FileText)} PDF
        </Button>
      )}
    </div>
  );
}

export function SinDatos({ puedeCargar }: { puedeCargar: boolean }): JSX.Element {
  return (
    <div className="rounded-lg bg-white p-6 text-sm text-neutral-600 ring-1 ring-neutral-200">
      {puedeCargar
        ? "Todavía no hay un libro diario cargado para este comité. Súbelo en Contabilidad · Cargas."
        : "La oficina contable todavía no publica información para este período."}
    </div>
  );
}
